import { forwardRef, useMemo } from 'react'
import { TextInput } from 'react-native'

import { FormInput, type FormInputProps } from '@/components'
import {
  TextArea as BaseTextArea,
  TextAreaInput as BaseTextAreaInput,
  type TextAreaInputProps as BaseTextAreaInputProps,
  type TextAreaProps as BaseTextAreaProps
} from '@/components/gluestack/textarea'

export interface TextAreaInputProps extends Omit<FormInputProps, 'children'> {
  onBlur?: () => void
  onChange: (value: string) => void
  textareaProps?: Omit<BaseTextAreaProps, 'children'>
  textareaInputProps?: Omit<BaseTextAreaInputProps, 'onChange' | 'value'>
  value?: string | null | undefined
}

export const TextAreaInput = forwardRef<TextInput, TextAreaInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      helper,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      textareaProps,
      textareaInputProps,
      value,
      ...props
    },
    ref
  ) => {
    const accessibilityLabel = useMemo(
      () => _accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`,
      [isRequired, label, helper, _accessibilityLabel]
    )

    return (
      <FormInput
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        <BaseTextArea {...textareaProps}>
          <BaseTextAreaInput
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{
              disabled: isDisabled
            }}
            aria-disabled={isDisabled}
            aria-label={accessibilityLabel}
            aria-required={isRequired}
            className='rounded bg-white font-poppinsRegular text-base text-black'
            onBlur={onBlur}
            onChangeText={onChange}
            // @ts-ignore this is complaining due to a bug in gluestack's type definitions
            ref={ref}
            value={value ?? ''}
            {...textareaInputProps}
          />
        </BaseTextArea>
      </FormInput>
    )
  }
)

TextAreaInput.displayName = 'TextAreaInput'
